import { all, call, put, takeLatest } from 'redux-saga/effects';
import { fetchClient } from '../utilities/fetchClient';
import ActionTypes from '../actions/actionTypes';
import {
  getScheduledReportsListSuccess,
  getScheduledReportsListFailure,
  getScheduledReportSuccess,
  getScheduledReportFailure,
  createScheduledReportSuccess,
  createScheduledReportFailure,
  updateScheduledReportSuccess,
  updateScheduledReportFailure,
  deleteScheduledReportSuccess,
  deleteScheduledReportFailure,
  enableScheduledReportSuccess,
  enableScheduledReportFailure,
  disableScheduledReportSuccess,
  disableScheduledReportFailure,
  getReportsListSuccess,
  getReportsListFailure,
  downloadReportSuccess,
  downloadReportFailure,
  deleteScheduledReportReset,
} from '../actions/reportsActions';
import Env from '../Env';
import fileDownload from 'js-file-download';
import { addToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';
import SURVEYS from 'constants/surveys';

// Scheduled Reports

function* getScheduledReportsList(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    const response = yield call(fetchClient, {
      url:
        `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}/schedules?` +
        `&limit=${action.payload.limit}&offset=${action.payload.offset}` +
        `&sortDirection=${action.payload.sortDirection}&sortColumn=${action.payload.sortColumn}` +
        `&searchTerm=${action.payload.searchTerm}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(getScheduledReportsListSuccess(response));
  } catch {
    yield put(getScheduledReportsListFailure());
  }
}

function* getScheduledReport(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;
    const reportScheduleId = action.payload.reportScheduleId;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    const response = yield call(fetchClient, {
      url: `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}/schedules/${reportScheduleId}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(getScheduledReportSuccess(response));
  } catch {
    yield put(getScheduledReportFailure());
  }
}

function* createScheduledReport(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;
    const request = action.payload.request;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    yield put(addToastAlert(Alert.CREATE_REPORT_LOADING));

    yield call(fetchClient, {
      url: `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}/schedules`,
      method: 'POST',
      headers: header,
      body: request,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(createScheduledReportSuccess());

    const { reportSchedule } = request || {};
    const isAdhoc =
      !reportSchedule ||
      (reportSchedule.scheduleFrequency == null &&
        reportSchedule.reportingDayOfWeek == null &&
        reportSchedule.scheduleReportingPeriod == null);

    if (isAdhoc) {
      yield put(addToastAlert(Alert.CREATE_ADHOC_REPORT_SUCCESS, 'success'));
    } else {
      yield put(addToastAlert(Alert.CREATE_REPORT_SUCCESS, 'success'));
    }

    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.CREATE_REPORT) {
      window._zf('startForm', { widgetId: SURVEYS.CREATE_REPORT, throttle: 'session' });
    }
  } catch {
    yield put(createScheduledReportFailure());
    yield put(addToastAlert(Alert.CREATE_REPORT_ERROR, 'error'));
  }
}

function* updateScheduledReport(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;
    const reportScheduleId = action.payload.reportScheduleId;
    const request = action.payload.request;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    yield put(addToastAlert(Alert.EDIT_REPORT_LOADING));

    yield call(fetchClient, {
      url: `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}/schedules/${reportScheduleId}`,
      method: 'PUT',
      headers: header,
      body: request,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(updateScheduledReportSuccess());
    yield put(addToastAlert(Alert.EDIT_REPORT_SUCCESS, 'success'));
  } catch {
    yield put(updateScheduledReportFailure());
    yield put(addToastAlert(Alert.EDIT_REPORT_ERROR, 'error'));
  }
}

function* deleteScheduledReport(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;
    const reportScheduleId = action.payload.reportScheduleId;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    yield put(addToastAlert(Alert.DELETE_REPORT_LOADING));

    yield call(fetchClient, {
      url: `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}/schedules/${reportScheduleId}`,
      method: 'DELETE',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(deleteScheduledReportSuccess());
    yield put(addToastAlert(Alert.DELETE_REPORT_SUCCESS, 'success'));
    yield put(deleteScheduledReportReset());
  } catch {
    yield put(deleteScheduledReportFailure());
    yield put(addToastAlert(Alert.DELETE_REPORT_ERROR, 'error'));
    yield put(deleteScheduledReportReset());
  }
}

function* enableScheduledReport(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;
    const reportScheduleId = action.payload.reportScheduleId;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    yield put(addToastAlert(Alert.ENABLE_REPORT_LOADING));

    yield call(fetchClient, {
      url: `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}/schedules/${reportScheduleId}/enable`,
      method: 'PUT',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(enableScheduledReportSuccess());
    yield put(addToastAlert(Alert.ENABLE_REPORT_SUCCESS, 'success'));
  } catch {
    yield put(enableScheduledReportFailure());
    yield put(addToastAlert(Alert.ENABLE_REPORT_ERROR, 'error'));
  }
}

function* disableScheduledReport(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;
    const reportScheduleId = action.payload.reportScheduleId;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    yield put(addToastAlert(Alert.DISABLE_REPORT_LOADING));

    yield call(fetchClient, {
      url: `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}/schedules/${reportScheduleId}/disable`,
      method: 'PUT',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(disableScheduledReportSuccess());
    yield put(addToastAlert(Alert.DISABLE_REPORT_SUCCESS, 'success'));
  } catch {
    yield put(disableScheduledReportFailure());
    yield put(addToastAlert(Alert.DISABLE_REPORT_ERROR, 'error'));
  }
}

// Reports

function* getReportsList(action) {
  try {
    const grandParentAccountNumber = action.payload.grandParentAccountNumber;
    const contractIdentifier = action.payload.contractIdentifier;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    const response = yield call(fetchClient, {
      url:
        `reports-api/v1/reports/${grandParentAccountNumber}/${contractIdentifier}?` +
        `&limit=${action.payload.limit}&offset=${action.payload.offset}` +
        `&sortDirection=${action.payload.sortDirection}&sortColumn=${action.payload.sortColumn}` +
        `&searchTerm=${action.payload.searchTerm}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(getReportsListSuccess(response));
  } catch {
    yield put(getReportsListFailure());
  }
}

function* downloadReport(action) {
  try {
    const { grandParentAccountNumber, contractIdentifier, documentId, reportName, contentType } = action.payload;

    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    const fileName = reportName + '_' + contractIdentifier + '.' + contentType.toLowerCase();

    yield put(addToastAlert(Alert.DOWNLOAD_REPORT_LOADING));
    const response = yield call(fetchClient, {
      url: `reports-api/v1/reports/invoicing/${grandParentAccountNumber}/${contractIdentifier}/downloadDocument/${documentId}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
      responseType: 'blob',
    });

    yield call(fileDownload, response, fileName);
    yield put(downloadReportSuccess());
    yield put(addToastAlert(Alert.DOWNLOAD_REPORT_SUCCESS, 'success'));
  } catch {
    yield put(downloadReportFailure());
    yield put(addToastAlert(Alert.DOWNLOAD_REPORT_ERROR, 'error'));
  }
}

export default function* reportsSaga() {
  yield all([
    takeLatest(ActionTypes.REPORTS.GET_SCHEDULED_REPORTS_LIST.REQUEST, getScheduledReportsList),
    takeLatest(ActionTypes.REPORTS.GET_SCHEDULED_REPORT.REQUEST, getScheduledReport),
    takeLatest(ActionTypes.REPORTS.CREATE_SCHEDULED_REPORT.REQUEST, createScheduledReport),
    takeLatest(ActionTypes.REPORTS.UPDATE_SCHEDULED_REPORT.REQUEST, updateScheduledReport),
    takeLatest(ActionTypes.REPORTS.DELETE_SCHEDULED_REPORT.REQUEST, deleteScheduledReport),
    takeLatest(ActionTypes.REPORTS.ENABLE_SCHEDULED_REPORT.REQUEST, enableScheduledReport),
    takeLatest(ActionTypes.REPORTS.DISABLE_SCHEDULED_REPORT.REQUEST, disableScheduledReport),
    takeLatest(ActionTypes.REPORTS.GET_REPORTS_LIST.REQUEST, getReportsList),
    takeLatest(ActionTypes.REPORTS.DOWNLOAD_REPORT.REQUEST, downloadReport),
  ]);
}
